import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, Plus, Trash2, PiggyBank } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'net_worth_data';

export const NetWorthCalculator = () => {
  const [assets, setAssets] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).assets : [
      { name: 'Cash & Savings', value: 25000 },
      { name: 'Property', value: 250000 },
      { name: 'Investments', value: 75000 },
      { name: 'Vehicle', value: 30000 }
    ];
  });

  const [liabilities, setLiabilities] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).liabilities : [
      { name: 'Mortgage', value: 180000 },
      { name: 'Credit Cards', value: 5000 },
      { name: 'Auto Loan', value: 15000 }
    ];
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ assets, liabilities }));
  }, [assets, liabilities]);

  const addAsset = () => {
    setAssets([...assets, { name: 'New Asset', value: 0 }]);
  };

  const removeAsset = (index) => {
    setAssets(assets.filter((_, i) => i !== index));
  };

  const updateAsset = (index, field, value) => {
    const newAssets = [...assets];
    newAssets[index][field] = field === 'value' ? parseFloat(value) || 0 : value;
    setAssets(newAssets);
  };

  const addLiability = () => {
    setLiabilities([...liabilities, { name: 'New Liability', value: 0 }]);
  };

  const removeLiability = (index) => {
    setLiabilities(liabilities.filter((_, i) => i !== index));
  };

  const updateLiability = (index, field, value) => {
    const newLiabilities = [...liabilities];
    newLiabilities[index][field] = field === 'value' ? parseFloat(value) || 0 : value;
    setLiabilities(newLiabilities);
  };

  const calculateNetWorth = () => {
    const totalAssets = assets.reduce((sum, asset) => sum + (parseFloat(asset.value) || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, liability) => sum + (parseFloat(liability.value) || 0), 0);
    const netWorth = totalAssets - totalLiabilities;
    const debtToAssetRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
    const liquidityRatio = calculateLiquidityRatio();
    const wealthScore = calculateWealthScore(netWorth, debtToAssetRatio, liquidityRatio);

    return {
      totalAssets,
      totalLiabilities,
      netWorth,
      debtToAssetRatio,
      liquidityRatio,
      wealthScore
    };
  };

  const calculateLiquidityRatio = () => {
    const liquidAssets = assets
      .filter(a => a.name.toLowerCase().includes('cash') || a.name.toLowerCase().includes('saving'))
      .reduce((sum, asset) => sum + (parseFloat(asset.value) || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, liability) => sum + (parseFloat(liability.value) || 0), 0);
    return totalLiabilities > 0 ? (liquidAssets / totalLiabilities) * 100 : 100;
  };

  const calculateWealthScore = (netWorth, debtRatio, liquidityRatio) => {
    let score = 50;
    
    if (netWorth > 0) score += 20;
    if (netWorth > 100000) score += 10;
    if (netWorth > 500000) score += 10;
    
    if (debtRatio < 30) score += 10;
    else if (debtRatio > 70) score -= 15;
    
    if (liquidityRatio > 50) score += 10;
    else if (liquidityRatio < 20) score -= 10;
    
    return Math.max(0, Math.min(100, score));
  };

  const generatePieData = () => {
    const data = [
      ...assets.map(a => ({ name: a.name, value: parseFloat(a.value) || 0, type: 'Asset' })),
      ...liabilities.map(l => ({ name: l.name, value: parseFloat(l.value) || 0, type: 'Liability' }))
    ];
    return data.filter(d => d.value > 0);
  };

  const generateComparisonData = () => {
    const results = calculateNetWorth();
    return [
      { name: 'Assets', value: results.totalAssets, fill: 'hsl(var(--secondary))' },
      { name: 'Liabilities', value: results.totalLiabilities, fill: 'hsl(var(--destructive))' },
      { name: 'Net Worth', value: Math.max(0, results.netWorth), fill: 'hsl(var(--primary))' }
    ];
  };

  const generateAllocationData = () => {
    return assets
      .filter(a => (parseFloat(a.value) || 0) > 0)
      .map((asset, index) => ({
        name: asset.name,
        value: parseFloat(asset.value) || 0,
        percentage: ((parseFloat(asset.value) || 0) / calculateNetWorth().totalAssets * 100).toFixed(1)
      }));
  };

  const results = calculateNetWorth();
  const pieData = generatePieData();
  const comparisonData = generateComparisonData();
  const allocationData = generateAllocationData();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-secondary';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-primary';
    return 'text-destructive';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Net Worth Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Net Worth?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Net worth is the total value of your assets minus your liabilities. It's a snapshot of your financial health 
                  and the most important number in personal finance. Track it over time to measure wealth building progress.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-display text-secondary">Assets</CardTitle>
                      <CardDescription>What you own</CardDescription>
                    </div>
                    <Button onClick={addAsset} size="sm" className="bg-secondary hover:bg-secondary/90">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {assets.map((asset, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={asset.name}
                          onChange={(e) => updateAsset(index, 'name', e.target.value)}
                          placeholder="Asset name"
                          className="text-sm"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <Input
                          type="number"
                          value={asset.value}
                          onChange={(e) => updateAsset(index, 'value', e.target.value)}
                          placeholder="0"
                          className="text-sm"
                        />
                      </div>
                      <Button 
                        onClick={() => removeAsset(index)} 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-muted-foreground">Total Assets</span>
                      <span className="text-xl font-display font-bold text-secondary">
                        {formatCurrency(results.totalAssets)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-display text-destructive">Liabilities</CardTitle>
                      <CardDescription>What you owe</CardDescription>
                    </div>
                    <Button onClick={addLiability} size="sm" className="bg-destructive hover:bg-destructive/90">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {liabilities.map((liability, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={liability.name}
                          onChange={(e) => updateLiability(index, 'name', e.target.value)}
                          placeholder="Liability name"
                          className="text-sm"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <Input
                          type="number"
                          value={liability.value}
                          onChange={(e) => updateLiability(index, 'value', e.target.value)}
                          placeholder="0"
                          className="text-sm"
                        />
                      </div>
                      <Button 
                        onClick={() => removeLiability(index)} 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-muted-foreground">Total Liabilities</span>
                      <span className="text-xl font-display font-bold text-destructive">
                        {formatCurrency(results.totalLiabilities)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Net Worth Summary</CardTitle>
                <CardDescription>Your overall financial position</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">Net Worth</p>
                    </div>
                    <p className={`text-3xl font-display font-bold ${
                      results.netWorth >= 0 ? 'text-primary' : 'text-destructive'
                    }`}>
                      {formatCurrency(results.netWorth)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {results.netWorth >= 0 ? 'Positive net worth' : 'Negative net worth'}
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                        <Calculator className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">Debt Ratio</p>
                    </div>
                    <p className="text-3xl font-display font-bold text-accent">
                      {results.debtToAssetRatio.toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {results.debtToAssetRatio < 30 ? 'Excellent' : results.debtToAssetRatio < 50 ? 'Good' : 'High debt'}
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                        <PiggyBank className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">Liquidity</p>
                    </div>
                    <p className="text-3xl font-display font-bold text-secondary">
                      {results.liquidityRatio.toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Cash coverage ratio
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">Health Score</p>
                    </div>
                    <p className={`text-3xl font-display font-bold ${getScoreColor(results.wealthScore)}`}>
                      {results.wealthScore}/100
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {getScoreLabel(results.wealthScore)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Asset vs Liability Comparison</CardTitle>
                <CardDescription className="text-base">
                  Visualize your financial position
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {comparisonData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Asset Allocation</CardTitle>
                  <CardDescription>
                    Distribution of your assets
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={allocationData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name}: ${percentage}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {allocationData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Financial Breakdown</CardTitle>
                  <CardDescription>
                    Detailed asset and liability list
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-secondary mb-3">Assets</h4>
                      <div className="space-y-2">
                        {allocationData.map((item, index) => (
                          <div key={index} className="flex justify-between items-center p-3 rounded-lg bg-secondary-light/50">
                            <span className="text-sm font-medium">{item.name}</span>
                            <span className="text-sm font-bold text-secondary">
                              {formatCurrency(item.value)} ({item.percentage}%)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <h4 className="font-semibold text-destructive mb-3">Liabilities</h4>
                      <div className="space-y-2">
                        {liabilities.filter(l => l.value > 0).map((item, index) => {
                          const percentage = ((parseFloat(item.value) || 0) / results.totalLiabilities * 100).toFixed(1);
                          return (
                            <div key={index} className="flex justify-between items-center p-3 rounded-lg bg-destructive/10">
                              <span className="text-sm font-medium">{item.name}</span>
                              <span className="text-sm font-bold text-destructive">
                                {formatCurrency(item.value)} ({percentage}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Net Worth</CardTitle>
                <CardDescription className="text-base">
                  Learn how to interpret and improve your financial position
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Your Financial Health Score: {results.wealthScore}/100
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your net worth is <strong className="text-primary">{formatCurrency(results.netWorth)}</strong>. 
                    {results.netWorth >= 0 ? (
                      <> This positive net worth indicates you own more than you owe, which is excellent for financial stability.</>
                    ) : (
                      <> This negative net worth means your liabilities exceed your assets. Focus on debt reduction and asset building.</>
                    )}
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">Net Worth = Total Assets - Total Liabilities</p>
                    <p className="font-mono">
                      Net Worth = {formatCurrency(results.totalAssets)} - {formatCurrency(results.totalLiabilities)} = {formatCurrency(results.netWorth)}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Debt-to-Asset Ratio: {results.debtToAssetRatio.toFixed(1)}%
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    This ratio shows what percentage of your assets are financed by debt. 
                    {results.debtToAssetRatio < 30 ? (
                      <> Your ratio is excellent! You have low leverage and strong financial flexibility.</>
                    ) : results.debtToAssetRatio < 50 ? (
                      <> Your ratio is acceptable but could be improved. Consider paying down high-interest debt.</>
                    ) : (
                      <> Your ratio is concerning. Focus on debt reduction to improve financial health.</>
                    )}
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">Risk Level:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        results.debtToAssetRatio < 30 ? 'bg-secondary/20 text-secondary' :
                        results.debtToAssetRatio < 50 ? 'bg-accent/20 text-accent' :
                        'bg-destructive/20 text-destructive'
                      }`}>
                        {results.debtToAssetRatio < 30 ? 'Low Risk' :
                         results.debtToAssetRatio < 50 ? 'Moderate Risk' :
                         'High Risk'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Industry standard: Below 30% is excellent, 30-50% is good, above 50% needs attention.
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Liquidity Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your liquidity ratio is <strong className="text-accent">{results.liquidityRatio.toFixed(1)}%</strong>. 
                    This measures your ability to cover liabilities with liquid assets (cash and savings). 
                    {results.liquidityRatio > 50 ? (
                      <> Excellent! You have strong cash reserves to handle emergencies.</>
                    ) : results.liquidityRatio > 20 ? (
                      <> Adequate but could be improved. Aim to build a 3-6 month emergency fund.</>
                    ) : (
                      <> Low liquidity. Prioritize building cash reserves for financial emergencies.</>
                    )}
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Wealth Concentration Risk
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Diversification:</strong> Avoid having more than 50% of your 
                        net worth in a single asset class. Spread investments across real estate, stocks, bonds, and cash.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Illiquid Assets:</strong> Real estate and retirement accounts 
                        are valuable but can't be quickly converted to cash. Maintain 3-6 months of expenses in liquid assets.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">High-Interest Debt:</strong> Credit cards and personal loans 
                        can erode wealth quickly. Prioritize paying off debt with interest rates above 10%.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Net Worth Improvement Roadmap
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Increase Assets</h4>
                      <p className="text-muted-foreground">
                        Invest consistently in diversified portfolios (stocks, bonds, real estate). Aim to save and invest 
                        at least 20% of your income monthly.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Reduce Liabilities</h4>
                      <p className="text-muted-foreground">
                        Pay down high-interest debt first (credit cards, personal loans). Consider debt consolidation 
                        or refinancing to lower interest rates.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Build Emergency Fund</h4>
                      <p className="text-muted-foreground">
                        Maintain 3-6 months of living expenses in a high-yield savings account. This protects you from 
                        taking on debt during emergencies.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Track Progress Monthly</h4>
                      <p className="text-muted-foreground">
                        Update your net worth calculation monthly or quarterly. Tracking shows progress and keeps you 
                        motivated toward financial goals.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NetWorthCalculator;
